
import { useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { createSavedCampaignInDb } from '@/hooks/campaign/services/db/savedCampaignService';

export const useRealtimeSync = () => {
  useEffect(() => {
    console.log('Setting up realtime sync for campaigns and saved_campaigns...');
    
    // Check authentication status
    const checkAuth = async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      console.log('Realtime sync auth status:', sessionData?.session ? 'Authenticated' : 'Not authenticated');
    };
    
    checkAuth();
    
    // Listen to new campaign creations
    const campaignsChannel = supabase
      .channel('campaigns-changes')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'campaigns'
        },
        (payload) => {
          console.log('Realtime campaign update received:', payload);
          
          // If it's a new campaign, ensure it gets added to saved_campaigns
          if (payload.eventType === 'INSERT' && payload.new) {
            console.log('New campaign created:', payload.new.id);
            
            // Extract necessary data
            const campaign = payload.new;
            createSavedCampaignFromInsert(campaign).catch(err => {
              console.error('Error creating saved campaign from insert:', err);
            });
          }
        }
      )
      .subscribe();
    
    // Also listen to saved_campaigns table for changes but don't show notifications
    const savedCampaignsChannel = supabase
      .channel('saved-campaigns-changes')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'saved_campaigns'
        },
        (payload) => {
          console.log('Realtime saved_campaigns update received:', payload);
          // Removed the toast notification here
        }
      )
      .subscribe();
    
    // Cleanup function
    return () => {
      console.log('Cleaning up realtime subscriptions...');
      supabase.removeChannel(campaignsChannel);
      supabase.removeChannel(savedCampaignsChannel);
    };
  }, []); // Empty dependency array - only run on mount/unmount
  
  // Helper function to create saved campaign from insert
  const createSavedCampaignFromInsert = async (campaign: any) => {
    try {
      // Check current auth state
      const { data: authData } = await supabase.auth.getSession();
      console.log('Creating saved campaign with auth status:', authData?.session ? 'Authenticated' : 'Not authenticated');
      
      // First check if a record already exists for this campaign
      const { data: existingData, error: checkError } = await supabase
        .from('saved_campaigns')
        .select('id')
        .eq('campaign_id', campaign.id)
        .limit(1);
      
      if (checkError) {
        console.error('Error checking for existing saved campaign:', checkError);
        return;
      }
      
      // If a record already exists, don't create another one
      if (existingData && existingData.length > 0) {
        console.log('Saved campaign already exists for this campaign ID - skipping duplicate creation');
        return;
      }
      
      // Extract selected locations with type safety
      const selectedLocations = campaign.selected_locations || [];
      const campaignName = localStorage.getItem('campaignName') || `Campaign ${new Date().toLocaleDateString()}`;
      
      // Create saved campaign with all necessary fields
      const savedCampaignData = {
        campaign_id: campaign.id,
        campaign_name: campaignName,
        media_owner_name: 'TableAds', // Default media owner if none specified
        devices_count: campaign.total_devices || 0,
        start_date: campaign.start_date,
        end_date: campaign.end_date,
        status: 'pending',
        location_name: selectedLocations.length > 0 ? selectedLocations[0].name : 'Unknown',
        creative_urls: campaign.creative_urls || [],
        brand_name: campaign.brand_name,
        company_name: campaign.company_name,
        person_in_charge: campaign.person_in_charge,
        job_title: campaign.job_title,
        email: campaign.email,
        mobile: campaign.mobile,
        total_budget: campaign.total_budget,
        terms_accepted: campaign.terms_accepted,
        selected_locations: selectedLocations,
        budget_info: campaign.budget_info,
        company_info: campaign.company_info
      };
      
      console.log('Creating saved campaign from realtime insert:', {
        campaign_id: savedCampaignData.campaign_id,
        media_owner_name: savedCampaignData.media_owner_name,
        locations_count: selectedLocations.length
      });
      
      try {
        const result = await createSavedCampaignInDb(savedCampaignData);
        
        if (result.error) {
          console.error('Error creating saved campaign via helper:', result.error);
        } else {
          console.log('Successfully created saved campaign via helper:', result.data);
        }
      } catch (e) {
        console.error('Failed to create saved campaign:', e);
      }
    } catch (err) {
      console.error('Exception in createSavedCampaignFromInsert:', err);
    }
  };
  
  return null;
};
